import {
  Checkbox,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
} from '@paywithextend/components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AmountInputField from 'src/components/AmountInputField/AmountInputField'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SingleDateInputField from 'src/components/SingleDateInputField/SingleDateInputField'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { ExtendCurrency } from 'src/enums'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CurrencyResponse,
  OrganizationReceiptRulesResponse,
} from 'src/models/organizations.models'
import { expensesTransactionsUrl } from 'src/navigation/urls'
import { GetReceiptRulesResponse } from 'src/queries/organizations/receiptRules/receiptRules.models'
import {
  mergeReceiptRuleIntoDetail,
  orgReceiptRulesMutations,
  orgReceiptRulesQueryKeys,
} from 'src/queries/organizations/receiptRules/receiptRules.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { toCents, toDollars } from 'src/utils/currency.utils'
import { parseBackendErrors } from 'src/utils/error.utils'
import { numberIsBelowMin } from 'src/utils/validation/number.validation'
import { css } from 'styled-components'

export type OrgSettingsReceiptRulesFields = {
  enabled?: boolean
  standardCardsEnabled?: boolean
  recurringCardsEnabled?: boolean
  physicalCardsEnabled?: boolean
  usdThresholdCents?: number
  cadThresholdCents?: number
  effectiveAt?: string
}

interface OrgSettingsReceiptRulesFormProps {
  closeReceiptRulesForm: () => void
  receiptRules: OrganizationReceiptRulesResponse | undefined
  currencies: CurrencyResponse[]
  hasRecurringCards: boolean
  orgId: string
}

const OrgSettingsReceiptRulesForm: React.FC<
  OrgSettingsReceiptRulesFormProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const queryClient = useQueryClient()
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const {
    receiptRules,
    currencies,
    closeReceiptRulesForm,
    orgId,
    hasRecurringCards,
  } = props

  // Data side effects live on the mutation's own onSuccess so they fire even
  // if the form unmounts before the request settles (mutate() callbacks do
  // not): merge the returned rule into the detail cache (reducer-merge parity)
  // and, when the form was opened from the transactions table, mark that list
  // stale. The UI side effect (closing the form) stays on the per-submit
  // mutate() callback in handleOnSubmit.
  const onReceiptRulesMutated = (
    data: { receiptRule: GetReceiptRulesResponse['receiptRule'] },
    variables: { orgId: string },
  ): void => {
    queryClient.setQueryData(
      orgReceiptRulesQueryKeys.detail(variables.orgId),
      (existing: GetReceiptRulesResponse | undefined) =>
        data.receiptRule
          ? mergeReceiptRuleIntoDetail(existing, data.receiptRule)
          : existing,
    )
    if (calculatedBackgroundLocation.pathname === expensesTransactionsUrl()) {
      queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.lists })
    }
  }

  const postMutation = useMutation({
    ...orgReceiptRulesMutations.post(),
    onSuccess: onReceiptRulesMutated,
  })
  const putMutation = useMutation({
    ...orgReceiptRulesMutations.put(),
    onSuccess: onReceiptRulesMutated,
  })
  const submittingReceiptRules = postMutation.isPending || putMutation.isPending
  const mutationError = postMutation.error ?? putMutation.error
  const { topLevelError, errorDetailMap } = useMemo(() => {
    if (!mutationError) {
      return { topLevelError: undefined, errorDetailMap: undefined }
    }
    const errors = parseBackendErrors(mutationError)
    return {
      topLevelError: errors.topLevelError,
      errorDetailMap: errors.errorDetailMap,
    }
  }, [mutationError])

  const manageReceiptRulesFieldsValidation =
    (): FormFieldsValidation<OrgSettingsReceiptRulesFields> => ({
      usdThresholdCents: [numberIsBelowMin(0)],
      cadThresholdCents: [numberIsBelowMin(0)],
    })

  const {
    fields: receiptRulesFlatFields,
    setFields: setReceiptRulesFlatFields,
    fieldsErrors: receiptRulesFlatFieldsErrors,
    onFieldChange: receiptRulesFlatFieldChange,
    setErrorsAndReturnDidPassValidation: receiptRulesFlatFieldsSetErrors,
  } = useFormFields<OrgSettingsReceiptRulesFields>(
    {},
    manageReceiptRulesFieldsValidation,
    errorDetailMap,
  )

  const hasCurrencyInOrg = (inputField: string): boolean => {
    let hasCurrencyInOrg = false
    currencies.some((currency) => {
      if (currency.inputField === inputField) {
        hasCurrencyInOrg = true
      }
    })

    return hasCurrencyInOrg
  }

  const handleOnCancel = (): void => {
    // parity with the deleted REMOVE_RECEIPT_RULES_FORM_ERROR action: clear
    // any submit error so reopening the form starts clean
    postMutation.reset()
    putMutation.reset()
    closeReceiptRulesForm()
  }

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    if (receiptRulesFlatFieldsSetErrors()) {
      const body = {
        ...receiptRulesFlatFields,
        usdThresholdCents:
          receiptRulesFlatFields.usdThresholdCents !== undefined
            ? toCents(receiptRulesFlatFields.usdThresholdCents)
            : undefined,
        cadThresholdCents:
          receiptRulesFlatFields.cadThresholdCents !== undefined
            ? toCents(receiptRulesFlatFields.cadThresholdCents)
            : undefined,
      }

      if (receiptRules === undefined || receiptRules === null) {
        postMutation.mutate(
          { orgId, body },
          { onSuccess: closeReceiptRulesForm },
        )
      } else {
        putMutation.mutate(
          { orgId, body },
          { onSuccess: closeReceiptRulesForm },
        )
      }

      analytics?.track('org settings create receipt rules', {
        enabled: receiptRulesFlatFields.enabled,
      })
    }
  }

  useEffect((): void => {
    setReceiptRulesFlatFields({
      ...receiptRules,
      usdThresholdCents:
        receiptRules?.usdThresholdCents !== undefined ||
        hasCurrencyInOrg('usdThresholdCents') ||
        currencies.length === 0
          ? toDollars(receiptRules?.usdThresholdCents || 0)
          : undefined,
      cadThresholdCents:
        receiptRules?.cadThresholdCents !== undefined ||
        hasCurrencyInOrg('cadThresholdCents')
          ? toDollars(receiptRules?.cadThresholdCents || 0)
          : undefined,
      enabled: !!receiptRules?.enabled,
      effectiveAt:
        receiptRules?.effectiveAt !== undefined
          ? receiptRules.effectiveAt
          : moment().format('YYYY-MM-DD'),
      standardCardsEnabled: !!receiptRules?.standardCardsEnabled,
      physicalCardsEnabled: !!receiptRules?.physicalCardsEnabled,
      recurringCardsEnabled: !!receiptRules?.recurringCardsEnabled,
    })
  }, [receiptRules])

  return (
    <div
      css={css`
        display: flex;
      `}
    >
      <form
        onSubmit={handleOnSubmit}
        css={css`
          display: flex;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 40px;
            `}
          >
            {translate(
              'An Organization Owner or Admin can change rules for the organization at any time. Cards will only be affected once Receipt Rules have been set. Visit our ${resourceCenter} to learn more.',
              {
                resourceCenter: (
                  <ResourceCenterLink
                    size={HoverableLinkSizes.Small}
                    underline
                    regular
                  />
                ),
              },
            )}
          </span>
          <span
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 8px;
            `}
          >
            {translate('Receipt Rules')}
          </span>
          <div
            css={css`
              display: flex;
              width: 175px;
            `}
          >
            <OnOffSwitch
              defaultOnOffState={receiptRules?.enabled}
              onChange={(val: boolean): void => {
                receiptRulesFlatFieldChange('enabled', val)
              }}
              testId={'receiptRulesSwitch'}
            />
          </div>
          {receiptRulesFlatFields['enabled'] && (
            <>
              <SingleDateInputField
                css={css`
                  margin-top: 32px;
                `}
                fieldLabel={translate('Start Date')}
                tooltip={
                  <Tooltip openLeft={false}>
                    {translate(
                      'The receipt rules will not be applied to transactions authorized before your start date',
                    )}
                  </Tooltip>
                }
                baseValue={moment(receiptRules?.effectiveAt)}
                errors={receiptRulesFlatFieldsErrors['effectiveAt']}
                dateMoment={moment(receiptRulesFlatFields['effectiveAt'])}
                onDateMomentChange={(val): void =>
                  receiptRulesFlatFieldChange(
                    'effectiveAt',
                    moment(val).format('YYYY-MM-DD'),
                  )
                }
                allowPastAndCurrentDates
                id={'receiptRulesStartDate'}
              />
              <span
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                  margin-top: 36px;
                  margin-bottom: 8px;
                `}
              >
                {translate('Minimum Transaction Amount')}
              </span>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  row-gap: 8px;
                `}
              >
                {(hasCurrencyInOrg('usdThresholdCents') ||
                  currencies.length === 0) && (
                  <AmountInputField
                    baseValue={
                      receiptRules?.usdThresholdCents !== undefined
                        ? toDollars(receiptRules.usdThresholdCents)
                        : 0
                    }
                    value={receiptRulesFlatFields['usdThresholdCents']}
                    onChange={(val: number | undefined): void => {
                      receiptRulesFlatFieldChange(
                        'usdThresholdCents',
                        val !== undefined ? val : 0,
                      )
                    }}
                    errors={receiptRulesFlatFieldsErrors['usdThresholdCents']}
                    maxValue={99999999}
                    currency={ExtendCurrency.Usd}
                    isRequest={false}
                    testId={'usdThresholdReceiptRules'}
                  />
                )}
                {hasCurrencyInOrg('cadThresholdCents') && (
                  <AmountInputField
                    baseValue={
                      receiptRules?.cadThresholdCents !== undefined
                        ? toDollars(receiptRules.cadThresholdCents)
                        : 0
                    }
                    value={receiptRulesFlatFields['cadThresholdCents']}
                    onChange={(val: number | undefined): void => {
                      receiptRulesFlatFieldChange(
                        'cadThresholdCents',
                        val !== undefined ? val : 0,
                      )
                    }}
                    errors={receiptRulesFlatFieldsErrors['cadThresholdCents']}
                    maxValue={99999999}
                    currency={ExtendCurrency.Cad}
                    isRequest={false}
                    testId={'cadThresholdReceiptRules'}
                  />
                )}
              </div>
              <span
                css={css`
                  margin-top: 36px;
                  margin-bottom: 8px;
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate('Card Types Affected')}
              </span>
              <span
                css={css`
                  ${getFontCss(
                    12,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {translate('At least one card type must be selected')}
              </span>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  margin-top: 16px;

                  & > *:nth-child(n + 2) {
                    margin-top: 12px;
                  }
                `}
              >
                <div>
                  <Checkbox
                    isSelected={
                      !!receiptRulesFlatFields['standardCardsEnabled']
                    }
                    onClick={(): void => {
                      receiptRulesFlatFieldChange(
                        'standardCardsEnabled',
                        !receiptRulesFlatFields['standardCardsEnabled'],
                      )
                    }}
                    label={translate('Standard Virtual Cards')}
                    testId={'standardCards'}
                  />
                  <div
                    css={css`
                      flex-grow: 1;
                    `}
                  />
                </div>
                {hasRecurringCards && (
                  <div>
                    <Checkbox
                      isSelected={
                        !!receiptRulesFlatFields['recurringCardsEnabled']
                      }
                      onClick={(): void => {
                        receiptRulesFlatFieldChange(
                          'recurringCardsEnabled',
                          !receiptRulesFlatFields['recurringCardsEnabled'],
                        )
                      }}
                      label={translate('Recurring Virtual Cards')}
                      testId={'recurringCards'}
                    />
                    <div
                      css={css`
                        flex-grow: 1;
                      `}
                    />
                  </div>
                )}
                <div>
                  <Checkbox
                    isSelected={
                      !!receiptRulesFlatFields['physicalCardsEnabled']
                    }
                    onClick={(): void => {
                      receiptRulesFlatFieldChange(
                        'physicalCardsEnabled',
                        !receiptRulesFlatFields['physicalCardsEnabled'],
                      )
                    }}
                    label={translate('Physical Cards')}
                    testId={'physicalCards'}
                  />
                  <div
                    css={css`
                      flex-grow: 1;
                    `}
                  />
                </div>
              </div>
            </>
          )}
          {topLevelError !== undefined && (
            <ErrorList
              errors={[topLevelError]}
              css={css`
                margin-top: ${topLevelError ? '40px' : '0px'};
                padding: 5px 5px;
              `}
            />
          )}
          <div
            css={css`
              margin-top: ${topLevelError ? '0px' : '40px'};
              display: flex;
              align-items: center;
            `}
          >
            <Button
              type={ButtonTypes.submit}
              design={ButtonDesigns.designCta}
              text={translate('Save Receipt Rules')}
              big
              loading={submittingReceiptRules}
              disabled={submittingReceiptRules}
              css={css`
                align-self: flex-start;
              `}
              testId={'saveReceiptRulesBtn'}
            />
            <HoverableLink
              ariaLabelSource={'Cancel'}
              onClick={handleOnCancel}
              css={css`
                margin-left: 20px;
              `}
              testId={'cancelReceiptRulesBtn'}
            >
              {translate('Cancel')}
            </HoverableLink>
          </div>
        </div>
      </form>
    </div>
  )
}

export default OrgSettingsReceiptRulesForm
